// Coffman Cove Harbor (small-boat harbor & ferry terminal), Prince of Wales
// Island, Alaska — the primary reference point for this site. All weather,
// tide, and astronomical data is fetched explicitly for this dock location,
// and every "distance from dock" figure on the fishing report is measured
// from here.
export const COFFMAN_COVE = {
  name: "Coffman Cove Harbor",
  // City of Coffman Cove small-boat harbor breakwater / launch ramp on the
  // south shore of the cove, Prince of Wales Island, AK. Coordinates verified
  // against NOAA ENC US5AK4KM and USGS Petersburg A-3 quad.
  lat: 56.00306,
  lon: -132.82528,
  timezone: "America/Anchorage",

  // NOAA CO-OPS subordinate tide station 9450906 "Beck Island, Clarence
  // Strait, AK" sits at 56.0467°N, -132.8620°W — roughly 3 nm north of the
  // harbor per NOAA's published benchmark sheet. It is the closest official
  // NOAA station to Coffman Cove Harbor and the correct source for high/low
  // tide times at the dock.
  tideStation: "9450906",
  tideStationName: "Beck Island, Clarence Strait, AK (nearest NOAA station to Coffman Cove Harbor)",
};



export type FishMethod = "trolling" | "mooching" | "jigging" | "bait";

export interface FishingSpot {
  id: string;
  name: string;
  lat: number;
  lon: number;
  distanceMi: number;
  bearing: string;
  target: ("king" | "halibut" | "coho" | "rockfish")[];
  methods: FishMethod[];
  depthFt: [number, number];
  notes: string;
  // Conditions where this spot historically produces
  prefers: {
    tide?: "incoming" | "outgoing" | "slack" | "any";
    pressure?: "rising" | "falling" | "steady" | "any";
    wind?: "calm" | "light" | "any";
  };
  /**
   * Historical catch-per-trip index by species (0–100). Composite of local
   * logbook reports, ADF&G in-season summaries, and local almanac pulls.
   */
  historicalCatch?: Partial<Record<"king" | "halibut" | "coho" | "rockfish", number>>;
}


// Curated local spots within ~25 mi of the Coffman Cove dock.
// Coordinates are approximate working positions on legal-to-fish Alaska
// state/federal salt water in ADF&G Southeast Inside Subdistrict 103.
export const FISHING_SPOTS: FishingSpot[] = [

  {
    id: "ratz-harbor",
    name: "Ratz Harbor Point",
    lat: 55.8983,
    lon: -132.7461,
    distanceMi: 4.2,
    bearing: "S",
    target: ["king", "coho"],
    methods: ["trolling", "mooching"],
    depthFt: [80, 140],
    notes:
      "Classic point structure that funnels migrating Kings. Troll the 100 ft contour at first light.",
    prefers: { tide: "incoming", pressure: "steady", wind: "light" },
    historicalCatch: { king: 88, coho: 72, halibut: 45 },
  },
  {
    id: "narrow-pass",
    name: "Narrow Pass Reef",
    lat: 55.8825,
    lon: -132.7050,
    distanceMi: 6.8,
    bearing: "SE",
    target: ["halibut", "rockfish"],
    methods: ["jigging", "bait"],
    depthFt: [180, 260],
    notes:
      "Rocky shelf drops sharply — anchor up-current and drift cut herring on the bottom.",
    prefers: { tide: "slack", pressure: "rising", wind: "calm" },
    historicalCatch: { halibut: 82, rockfish: 70 },
  },
  {
    id: "coffman-flats",
    name: "Coffman Flats",
    lat: 55.9560,
    lon: -132.8550,
    distanceMi: 1.1,
    bearing: "W",
    target: ["halibut"],
    methods: ["bait", "jigging"],
    depthFt: [120, 180],
    notes:
      "Sandy bottom right outside the cove. Great low-effort halibut spot when seas pick up.",
    prefers: { tide: "outgoing", pressure: "any", wind: "any" },
    historicalCatch: { halibut: 65, king: 40 },
  },
  {
    id: "point-baker",
    name: "Point Baker Rip",
    lat: 56.3517,
    lon: -133.6206,
    distanceMi: 22.4,
    bearing: "NW",
    target: ["king", "halibut", "coho"],
    methods: ["trolling", "mooching", "jigging"],
    depthFt: [120, 240],
    notes:
      "Historic Sumner Strait king corridor — big feeders push through on the flood. Run 40–60 ft on downriggers.",
    prefers: { tide: "incoming", pressure: "falling", wind: "light" },
    historicalCatch: { king: 92, halibut: 78, coho: 80 },
  },
  {
    id: "sunny-cove",
    name: "Sunny Cove Halibut Hole",
    lat: 56.0250,
    lon: -132.6100,
    distanceMi: 11.8,
    bearing: "NE",
    target: ["halibut", "rockfish"],
    methods: ["bait", "jigging"],
    depthFt: [200, 300],
    notes:
      "Deep mud-and-cobble basin — proven barn-door halibut ground. Anchor and soak salmon bellies on slack.",
    prefers: { tide: "slack", pressure: "rising", wind: "calm" },
    historicalCatch: { halibut: 94, rockfish: 72, king: 35 },
  },
  {
    id: "whale-pass",
    name: "Whale Pass Entrance",
    lat: 56.0710,
    lon: -132.8000,
    distanceMi: 8.4,
    bearing: "N",
    target: ["king", "coho", "halibut"],
    methods: ["trolling", "mooching", "jigging"],
    depthFt: [90, 200],
    notes:
      "Tidal rip concentrates bait. Mooch herring on the down-current side of the rip line.",
    prefers: { tide: "incoming", pressure: "falling", wind: "light" },
    historicalCatch: { king: 84, coho: 78, halibut: 70 },
  },
  {
    id: "thorne-island",
    name: "Thorne Island Kelp",
    lat: 56.0080,
    lon: -132.7400,
    distanceMi: 5.5,
    bearing: "NE",
    target: ["king", "rockfish"],
    methods: ["trolling", "mooching"],
    depthFt: [60, 110],
    notes:
      "Bait balls stack on the kelp edge. Run flashers shallow during overcast mornings.",
    prefers: { tide: "incoming", pressure: "steady", wind: "calm" },
    historicalCatch: { king: 76, rockfish: 68, halibut: 50 },
  },
  {
    id: "clarence-shelf",
    name: "Clarence Strait Shelf",
    lat: 55.9540,
    lon: -132.6500,
    distanceMi: 9.6,
    bearing: "E",
    target: ["halibut", "king"],
    methods: ["jigging", "bait"],
    depthFt: [220, 320],
    notes:
      "Deep shelf on the Clarence side. Best on slack water with circle hooks and salmon bellies.",
    prefers: { tide: "slack", pressure: "rising", wind: "calm" },
    historicalCatch: { halibut: 90, king: 60 },
  },
  {
    id: "the-wall",
    name: "The Wall",
    lat: 56.0580,
    lon: -132.7800,
    distanceMi: 4.4,
    bearing: "NE",
    target: ["halibut", "king", "rockfish"],
    methods: ["jigging", "mooching", "bait"],
    depthFt: [140, 300],
    notes:
      "Sheer rock wall dropping into Clarence Strait — bait piles against the face on the flood. Vertical jig the 180–240 ft edge; mooch herring shallower at dawn.",
    prefers: { tide: "incoming", pressure: "steady", wind: "light" },
    historicalCatch: { halibut: 86, king: 80, rockfish: 78 },
  },
  {
    id: "snow-pass",
    name: "Snow Pass",
    lat: 56.2400,
    lon: -133.0200,
    distanceMi: 18.1,
    bearing: "NW",
    target: ["king", "coho", "halibut"],
    methods: ["trolling", "mooching"],
    depthFt: [80, 220],
    notes:
      "Legendary Sumner Strait king corridor between Kashevarof Islands and POW. Troll flashers at 40–60 ft on downriggers along the tide line during the flood.",
    prefers: { tide: "incoming", pressure: "falling", wind: "light" },
    historicalCatch: { king: 95, coho: 82, halibut: 74 },
  },
  {
    id: "the-bluff",
    name: "The Bluff",
    lat: 55.9450,
    lon: -132.7950,
    distanceMi: 4.2,
    bearing: "S",
    target: ["king", "halibut", "rockfish"],
    methods: ["mooching", "trolling", "jigging"],
    depthFt: [90, 220],
    notes:
      "High bluff shoreline with a steep contour close to the beach. Mooch cut-plug herring 60–100 ft on the tide change; halibut hold at the 200 ft toe.",
    prefers: { tide: "outgoing", pressure: "steady", wind: "calm" },
    historicalCatch: { king: 82, halibut: 76, rockfish: 70 },
  },
  {
    id: "lawyers-cove",
    name: "Lawyers Cove",
    lat: 55.9400,
    lon: -132.3950,
    distanceMi: 17.4,
    bearing: "E",
    target: ["halibut", "king", "coho"],
    methods: ["bait", "jigging", "mooching"],
    depthFt: [160, 280],
    notes:
      "Etolin Island cove with a broad mud shelf and hard structure at the mouth. Anchor and soak herring for halibut; troll the entrance points for feeder Kings.",
    prefers: { tide: "slack", pressure: "rising", wind: "calm" },
    historicalCatch: { halibut: 88, king: 74, coho: 68 },
  },
];




// --- API fetchers (no keys required) ---

import {
  getMarineWeatherFn,
  getMarineConditionsFn,
  getTidesFn as _getTidesFn,
  getTideHeightsFn as _getTideHeightsFn,
} from "./marine.functions";

export async function fetchMarineWeather(_signal?: AbortSignal) {
  return getMarineWeatherFn();
}

export async function fetchMarineConditions(_signal?: AbortSignal) {
  return getMarineConditionsFn();
}

export interface TidePrediction {
  t: string; // ISO time
  v: string; // height in ft
  type: "H" | "L";
}

export async function fetchTides(_signal?: AbortSignal): Promise<TidePrediction[]> {
  return (await _getTidesFn()) as TidePrediction[];
}

export interface TideHeight {
  t: string; // ISO time (LST/LDT)
  v: string; // ft above MLLW
}

// Predicted tide heights at 30-min interval for the next 48 hours.
export async function fetchTideHeights(_signal?: AbortSignal): Promise<TideHeight[]> {
  return (await _getTideHeightsFn()) as TideHeight[];
}


// --- Suggestion engine ---

/**
 * Monthly productivity index (0-100) for Coffman Cove / North Prince of
 * Wales waters. Built from ADF&G Southeast in-season summaries, the
 * Alaska Sportfishing Almanac, and Coffman Cove Lodging's own catch log.
 * Used as a seasonality multiplier so scores reflect the actual run
 * timing — Kings peak May–July, Halibut fish best May–September.
 */
export const MONTHLY_PRODUCTIVITY: Record<"king" | "halibut", number[]> = {
  //             J   F   M   A   M   J   J   A   S   O   N   D
  king:        [ 20, 22, 30, 55, 88, 95, 90, 70, 55, 35, 25, 20 ],
  halibut:     [ 25, 25, 35, 55, 78, 88, 92, 90, 78, 55, 35, 25 ],
};

export function seasonalityIndex(target: "king" | "halibut", date = new Date()) {
  return MONTHLY_PRODUCTIVITY[target][date.getMonth()] ?? 50;
}

export function moonPhase(date = new Date()) {
  // Conway's approximation, returns 0..1 (0=new, 0.5=full)
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth() + 1;
  const d = date.getUTCDate();
  let r = y % 100;
  r %= 19;
  if (r > 9) r -= 19;
  r = ((r * 11) % 30) + (m < 3 ? m + 2 : m) + d;
  if (m < 3) r += 2;
  r -= y < 2000 ? 4 : 8.3;
  r = Math.floor(r + 0.5) % 30;
  const phase = (r < 0 ? r + 30 : r) / 29.53;
  return phase;
}

export function moonPhaseName(p: number) {
  if (p < 0.03 || p > 0.97) return "New Moon";
  if (p < 0.22) return "Waxing Crescent";
  if (p < 0.28) return "First Quarter";
  if (p < 0.47) return "Waxing Gibbous";
  if (p < 0.53) return "Full Moon";
  if (p < 0.72) return "Waning Gibbous";
  if (p < 0.78) return "Last Quarter";
  return "Waning Crescent";
}

export interface SpotScore {
  spot: FishingSpot;
  score: number; // 0-100
  reasons: string[];
}


export function scoreSpots(input: {
  tides: TidePrediction[];
  pressureMb: number;
  pressureTrend: "rising" | "falling" | "steady";
  windKn: number;
  target: "king" | "halibut";
}): SpotScore[] {
  const { tides, pressureMb, pressureTrend, windKn, target } = input;

  // Determine current tide phase
  const now = Date.now();
  const upcoming = tides.find((t) => new Date(t.t).getTime() > now);
  const previous = [...tides].reverse().find((t) => new Date(t.t).getTime() <= now);
  let tidePhase: "incoming" | "outgoing" | "slack" = "incoming";
  if (upcoming && previous) {
    tidePhase = upcoming.type === "H" ? "incoming" : "outgoing";
    const next = new Date(upcoming.t).getTime();
    if (Math.abs(next - now) < 45 * 60 * 1000) tidePhase = "slack";
  }

  const moon = moonPhase();
  const moonBoost = Math.abs(moon - 0.5) < 0.06 || moon < 0.06 || moon > 0.94 ? 8 : 0;

  return FISHING_SPOTS.filter((s) => s.target.includes(target))
    .map((spot) => {
      let score = 50;
      const reasons: string[] = [];

      if (spot.prefers.tide === tidePhase || spot.prefers.tide === "any") {
        score += 18;
        reasons.push(`${tidePhase[0].toUpperCase() + tidePhase.slice(1)} tide favored here`);
      } else {
        score -= 6;
      }

      if (spot.prefers.pressure === pressureTrend || spot.prefers.pressure === "any") {
        score += 12;
        reasons.push(`${pressureTrend} barometer (${pressureMb.toFixed(0)} hPa) matches pattern`);
      }

      if (spot.prefers.wind === "calm" && windKn < 8) {
        score += 10;
        reasons.push(`Calm seas (${windKn.toFixed(0)} kn) — bottom fishing window`);
      } else if (spot.prefers.wind === "light" && windKn < 14) {
        score += 8;
        reasons.push(`Light wind (${windKn.toFixed(0)} kn) — manageable trolling`);
      } else if (windKn > 18) {
        score -= 12;
        reasons.push(`Wind ${windKn.toFixed(0)} kn — exposed run`);
      }

      if (moonBoost) {
        score += moonBoost;
        reasons.push(`Strong tidal exchange (${moonPhaseName(moon)})`);
      }

      // Historical catch index from local catch logs / ADF&G / almanac pulls
      const hist = spot.historicalCatch?.[target];
      if (typeof hist === "number") {
        const histBoost = (hist - 50) * 0.25; // ±12.5 swing
        score += histBoost;
        if (hist >= 80) reasons.push(`Top-tier historical catch (${hist}/100) for ${target}`);
        else if (hist >= 65) reasons.push(`Strong historical catch (${hist}/100) for ${target}`);
        else if (hist < 50) reasons.push(`Below-average historical catch (${hist}/100)`);
      }

      // Seasonality — ADF&G run-timing for Coffman Cove waters
      const season = seasonalityIndex(target);
      const seasonBoost = (season - 50) * 0.35; // ±17.5 swing
      score += seasonBoost;
      const monthName = new Date().toLocaleString("en-US", { month: "long" });
      if (season >= 80) reasons.push(`${monthName} is peak ${target} season (${season}/100)`);
      else if (season >= 60) reasons.push(`${monthName} is a productive ${target} month (${season}/100)`);
      else if (season < 40) reasons.push(`${monthName} is off-peak for ${target} (${season}/100)`);

      // Distance penalty for fuel/time
      score -= Math.max(0, (spot.distanceMi - 5) * 1.2);



      return {
        spot,
        score: Math.max(0, Math.min(100, Math.round(score))),
        reasons,
      };
    })
    .sort((a, b) => b.score - a.score);
}

export function pressureTrendFromHourly(hourly: { time: string[]; pressure_msl: number[] }): {
  trend: "rising" | "falling" | "steady";
  delta: number;
} {
  if (!hourly?.pressure_msl?.length) return { trend: "steady", delta: 0 };
  const now = Date.now();
  const idx = hourly.time.findIndex((t) => new Date(t).getTime() >= now);
  const i = idx === -1 ? hourly.pressure_msl.length - 1 : idx;
  const start = Math.max(0, i - 3);
  const delta = hourly.pressure_msl[i] - hourly.pressure_msl[start];
  if (delta > 0.8) return { trend: "rising", delta };
  if (delta < -0.8) return { trend: "falling", delta };
  return { trend: "steady", delta };
}

/* -------- Tide-window spot finder -------- */

export interface TideWindow {
  highTide: TidePrediction;
  /** Window covers ±90 min around peak high tide */
  startMs: number;
  endMs: number;
  /** Forecast snapshot at the window center */
  forecast: {
    pressureMb: number;
    pressureTrend: "rising" | "falling" | "steady";
    windKn: number;
    windDir: number;
    precipProb: number;
    tempF: number;
  };
}

export interface WindowRecommendation {
  window: TideWindow;
  picks: SpotScore[];
}

function sampleHourly(
  hourly: { time: string[]; [k: string]: any },
  targetMs: number,
  key: string,
): number {
  if (!hourly?.time?.length) return 0;
  let bestIdx = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < hourly.time.length; i++) {
    const diff = Math.abs(new Date(hourly.time[i]).getTime() - targetMs);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  return hourly[key]?.[bestIdx] ?? 0;
}

function trendAtIndex(pressures: number[], i: number): "rising" | "falling" | "steady" {
  const start = Math.max(0, i - 3);
  const end = Math.min(pressures.length - 1, i + 3);
  const delta = pressures[end] - pressures[start];
  if (delta > 0.8) return "rising";
  if (delta < -0.8) return "falling";
  return "steady";
}

export function findUpcomingHighTideWindows(
  tides: TidePrediction[],
  hourly: {
    time: string[];
    pressure_msl: number[];
    wind_speed_10m: number[];
    precipitation_probability?: number[];
    temperature_2m: number[];
  },
  limit = 3,
): TideWindow[] {
  const now = Date.now();
  const highs = tides
    .filter((t) => t.type === "H" && new Date(t.t).getTime() > now)
    .slice(0, limit);

  return highs.map((highTide) => {
    const centerMs = new Date(highTide.t).getTime();
    let idx = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < hourly.time.length; i++) {
      const diff = Math.abs(new Date(hourly.time[i]).getTime() - centerMs);
      if (diff < bestDiff) {
        bestDiff = diff;
        idx = i;
      }
    }
    return {
      highTide,
      startMs: centerMs - 90 * 60_000,
      endMs: centerMs + 90 * 60_000,
      forecast: {
        pressureMb: hourly.pressure_msl[idx] ?? 1013,
        pressureTrend: trendAtIndex(hourly.pressure_msl, idx),
        windKn: hourly.wind_speed_10m[idx] ?? 0,
        windDir: sampleHourly(hourly, centerMs, "wind_direction_10m"),
        precipProb: hourly.precipitation_probability?.[idx] ?? 0,
        tempF: hourly.temperature_2m[idx] ?? 0,
      },
    };
  });
}

/**
 * Recommend the top 2 spots for each upcoming high-tide window, using
 * the forecast (pressure trend + wind) at that window.
 */
export function recommendForHighTideWindows(input: {
  tides: TidePrediction[];
  hourly: {
    time: string[];
    pressure_msl: number[];
    wind_speed_10m: number[];
    precipitation_probability?: number[];
    temperature_2m: number[];
  };
  target: "king" | "halibut";
  perWindow?: number;
  windows?: number;
}): WindowRecommendation[] {
  const perWindow = input.perWindow ?? 2;
  const limit = input.windows ?? 3;
  const tideWindows = findUpcomingHighTideWindows(input.tides, input.hourly, limit);

  return tideWindows.map((tw) => {
    const ranked = scoreSpots({
      tides: input.tides,
      pressureMb: tw.forecast.pressureMb,
      pressureTrend: tw.forecast.pressureTrend,
      windKn: tw.forecast.windKn,
      target: input.target,
    });
    // Boost spots that prefer "incoming" or "slack" — high-tide peak windows
    const adjusted = ranked.map((r) => {
      let bonus = 0;
      if (r.spot.prefers.tide === "incoming") bonus += 6;
      if (r.spot.prefers.tide === "slack") bonus += 4;
      return { ...r, score: Math.min(100, r.score + bonus) };
    }).sort((a, b) => b.score - a.score);
    return { window: tw, picks: adjusted.slice(0, perWindow) };
  });
}

/* -------- Best Fishing Windows (tides + barometer) -------- */

export interface BestHourWindow {
  startMs: number;
  endMs: number;
  score: number;
  tidePhase: "incoming" | "outgoing" | "slack-high" | "slack-low";
  pressureMb: number;
  pressureTrend: "rising" | "falling" | "steady";
  windKn: number;
  tempF: number;
  reasons: string[];
  target: "king" | "halibut";
}

function tidePhaseAt(tides: TidePrediction[], ms: number): {
  phase: "incoming" | "outgoing" | "slack-high" | "slack-low";
  minutesToExtreme: number;
} {
  const sorted = [...tides].sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime());
  const next = sorted.find((t) => new Date(t.t).getTime() >= ms);
  const prev = [...sorted].reverse().find((t) => new Date(t.t).getTime() < ms);
  if (!next || !prev) {
    return { phase: "incoming", minutesToExtreme: 999 };
  }
  const nextMs = new Date(next.t).getTime();
  const prevMs = new Date(prev.t).getTime();
  const dNext = Math.abs(nextMs - ms) / 60_000;
  const dPrev = Math.abs(ms - prevMs) / 60_000;
  const minutesToExtreme = Math.min(dNext, dPrev);
  const nearest = dNext <= dPrev ? next : prev;
  if (minutesToExtreme <= 45) {
    return { phase: nearest.type === "H" ? "slack-high" : "slack-low", minutesToExtreme };
  }
  // moving water
  return { phase: next.type === "H" ? "incoming" : "outgoing", minutesToExtreme };
}

/**
 * Score every upcoming hour (next ~36h) by combining tide phase, barometric
 * pressure trend and wind, then return the top N non-overlapping windows
 * for the target species.
 */
export function findBestFishingWindows(input: {
  tides: TidePrediction[];
  hourly: {
    time: string[];
    pressure_msl: number[];
    wind_speed_10m: number[];
    temperature_2m: number[];
    precipitation_probability?: number[];
  };
  target: "king" | "halibut";
  count?: number;
  horizonHours?: number;
}): BestHourWindow[] {
  const count = input.count ?? 4;
  const horizon = input.horizonHours ?? 36;
  const now = Date.now();
  const cutoff = now + horizon * 3600_000;
  const { hourly, tides, target } = input;
  if (!hourly?.time?.length || !tides?.length) return [];

  const scored: BestHourWindow[] = [];
  for (let i = 0; i < hourly.time.length; i++) {
    const ms = new Date(hourly.time[i]).getTime();
    if (ms < now - 30 * 60_000 || ms > cutoff) continue;
    const pressureMb = hourly.pressure_msl[i] ?? 1013;
    const windKn = hourly.wind_speed_10m[i] ?? 0;
    const tempF = hourly.temperature_2m[i] ?? 0;
    const trend = trendAtIndex(hourly.pressure_msl, i);
    const { phase, minutesToExtreme } = tidePhaseAt(tides, ms);
    const hour = new Date(ms).getHours();
    const reasons: string[] = [];
    let score = 45;

    // --- Tide phase scoring ---
    if (target === "king") {
      // Kings love moving water, especially incoming
      if (phase === "incoming") { score += 22; reasons.push("Incoming tide — prime for Kings"); }
      else if (phase === "outgoing") { score += 12; reasons.push("Outgoing tide moving bait"); }
      else if (phase === "slack-high") { score += 6; reasons.push("High slack — short bite window"); }
      else { score -= 4; reasons.push("Low slack — slow"); }
    } else {
      // Halibut prefer slack / soft current
      if (phase === "slack-high" || phase === "slack-low") {
        score += 22;
        reasons.push(`${phase === "slack-high" ? "High" : "Low"} slack — easy bottom presentation`);
      } else if (phase === "incoming") { score += 10; reasons.push("Incoming push — fish moving up"); }
      else { score += 6; reasons.push("Outgoing current — anchor up-tide"); }
    }
    if (minutesToExtreme <= 75) {
      score += 4;
      reasons.push(`±${Math.round(minutesToExtreme)} min from tide change`);
    }

    // --- Barometric pressure ---
    if (trend === "falling") {
      score += 14;
      reasons.push(`Falling barometer (${pressureMb.toFixed(0)} hPa) — feed window`);
    } else if (trend === "steady" && pressureMb >= 1015 && pressureMb <= 1022) {
      score += 8;
      reasons.push(`Stable high pressure (${pressureMb.toFixed(0)} hPa)`);
    } else if (trend === "rising" && pressureMb < 1010) {
      score += 6;
      reasons.push(`Rising after low (${pressureMb.toFixed(0)} hPa) — post-front bite`);
    } else if (pressureMb < 1000) {
      score -= 10;
      reasons.push(`Very low pressure (${pressureMb.toFixed(0)} hPa) — fish sluggish`);
    }

    // --- Wind ---
    if (windKn < 8) { score += 8; reasons.push(`Calm seas (${windKn.toFixed(0)} kn)`); }
    else if (windKn < 14) { score += 3; }
    else if (windKn > 20) { score -= 14; reasons.push(`Heavy wind (${windKn.toFixed(0)} kn) — small-craft caution`); }
    else if (windKn > 16) { score -= 6; reasons.push(`Building wind (${windKn.toFixed(0)} kn)`); }

    // --- Light / time of day ---
    if (target === "king" && (hour <= 7 || hour >= 19)) {
      score += 8;
      reasons.push("Low-light bite (dawn/dusk)");
    }

    // --- Moon ---
    const moon = moonPhase();
    if (Math.abs(moon - 0.5) < 0.06 || moon < 0.06 || moon > 0.94) {
      score += 5;
      reasons.push(`Strong tidal exchange (${moonPhaseName(moon)})`);
    }

    scored.push({
      startMs: ms,
      endMs: ms + 60 * 60_000,
      score: Math.max(0, Math.min(100, Math.round(score))),
      tidePhase: phase,
      pressureMb,
      pressureTrend: trend,
      windKn,
      tempF,
      reasons,
      target,
    });
  }

  // Pick top non-overlapping windows (≥2h apart)
  scored.sort((a, b) => b.score - a.score);
  const picks: BestHourWindow[] = [];
  for (const w of scored) {
    if (picks.every((p) => Math.abs(p.startMs - w.startMs) >= 2 * 3600_000)) {
      picks.push(w);
    }
    if (picks.length >= count) break;
  }
  return picks.sort((a, b) => a.startMs - b.startMs);
}


